import {connect} from 'react-redux';

import {isElectron} from '../../../libs/shared/core/src/helpers/environment/platformHelpers';
import {
  buildContactForHandle,
  buildContactFromContactUid,
  buildContactUid,
  ContactModel,
  ContactSourcesEnum,
} from '../../../libs/shared/core/src/models/api/contactModel';
import {RecipientRolesEnum} from '../../../libs/shared/core/src/models/api/recipientModel';
import {TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {globalNamespace} from '../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {buildTeammateDisplayName} from '../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {ResourceMap} from '../../../libs/shared/core/src/store/commonAppTypes';
import {makeCalendarsPanelId, ViewerPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {makeMapDispatchToProps, makeMapStateToProps} from '../../../srcCommon/helpers/reselectHelpers';
import {createVisualContactModel} from '../../helpers/models/contactHelpers';
import {VisualContactModel} from '../../models/api/visualContactModel';
import {VisualRecipientModel} from '../../models/api/visualRecipientModel';
import {ThunkAction} from '../../store/appThunk';
import {composeCommentAction} from '../../store/data/comments/commentsActions';
import {getContacts} from '../../store/data/dataSelectors';
import {composeMessageAction} from '../../store/data/messages/draftMessagesActions';
import {createOrUpdateCalendarEventDraftAction} from '../../store/panels/calendars/calendarsPanelActions';
import {selectSideCalendarActionSync} from '../../store/panels/calendars/sideCalendarsPanelActions';
import {TeammateCardFooterActions} from './teammateCardFooterActions';

/*
 * Props.
 */

interface TeammateCardFooterActionsContainerProps {
  panelId: ViewerPanelId;
  teammate: TeammateModel;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TeammateCardFooterActionsContainerProps>()(() => ({
  allContacts: getContacts,
}));

function createVisualContactModelFromTeammate(
  teammate: TeammateModel,
  namespace: string,
  allContacts: ResourceMap<ContactModel>,
) {
  const contactUid = buildContactUid(ContactSourcesEnum.EMAIL, teammate.email);
  const contact = allContacts[contactUid];

  return createVisualContactModel({
    contact: !contact ? buildContactFromContactUid(contactUid) : contact,
    namespace,
    name: buildTeammateDisplayName(teammate),
    fallbackAvatarUrl: teammate.avatarUrl,
    teammate,
    shouldAllowContactsWithoutUrls: true,
  });
}

const mapDispatchToProps = makeMapDispatchToProps<TeammateCardFooterActionsContainerProps>()((props) => {
  const {teammate, panelId} = props;
  return {
    onComposeDiscussion: () => composeCommentAction({teammateIds: [teammate.id]}),
    onComposeMessage: () => {
      // The namespace doesn't matter here since we're just opening the composer.
      const visualRecipient: VisualRecipientModel = {
        contact: buildContactForHandle(ContactSourcesEnum.EMAIL, teammate.email),
        namespace: globalNamespace,
        role: RecipientRolesEnum.TO,
      };
      return composeMessageAction({recipients: [visualRecipient], shouldForcePopout: isElectron()});
    },
    openAndCreateEvent:
      (contacts: ReadonlyArray<VisualContactModel>): ThunkAction =>
      async (dispatch) => {
        const calendarPanelId = makeCalendarsPanelId(panelId);
        dispatch(selectSideCalendarActionSync(calendarPanelId));
        dispatch(createOrUpdateCalendarEventDraftAction(calendarPanelId, contacts));
      },
  };
});

export const TeammateCardFooterActionsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    onCreateEvent: () => {
      const contact = createVisualContactModelFromTeammate(
        ownProps.teammate,
        globalNamespace,
        stateProps.allContacts,
      );
      const contacts = contact ? [contact] : [];
      dispatchProps.openAndCreateEvent(contacts);
    },
  }),
)(TeammateCardFooterActions);
